import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Descriptions,
    Empty,
    Form,
    Input,
    Result,
    Row,
    Space,
    Spin,
    Divider,
    Steps,
    Table,
    Tag,
    Tooltip,
    Typography,
    Upload,
} from "antd";

import {
    CheckCircleFilled,
    DeleteOutlined,
    EyeOutlined,
    FilePdfOutlined,
    InboxOutlined,
    ReloadOutlined,
    UploadOutlined,
} from "@ant-design/icons";

import Swal from "sweetalert2";

import api from "../api/api";
import { useAcademicYear } from "../AcademicYearContext";

import CurriculumPreviewPopup from "./CurriculumPreviewPopup";

const { Dragger } = Upload;
const { Text, Title } = Typography;


// ============================================================
// HELPERS
// ============================================================

const getErrorMessage = (
    error,
    fallback = "Something went wrong."
) => {
    const data = error?.response?.data;

    if (typeof data === "string") {
        return data;
    }

    if (data?.detail) {
        return data.detail;
    }

    if (data?.message) {
        return data.message;
    }

    if (data?.error) {
        return data.error;
    }

    if (data && typeof data === "object") {
        const messages = Object.entries(data)
            .map(([field, value]) => {
                let message = value;

                if (Array.isArray(value)) {
                    message = value.join(", ");
                } else if (
                    value &&
                    typeof value === "object"
                ) {
                    message =
                        JSON.stringify(value);
                }

                return `${field}: ${message}`;
            })
            .filter(Boolean);

        if (messages.length) {
            return messages.join("\n");
        }
    }

    return (
        error?.message ||
        fallback
    );
};


const extractList = (payload) => {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (Array.isArray(payload?.results)) {
        return payload.results;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    if (
        Array.isArray(
            payload?.data?.results
        )
    ) {
        return payload.data.results;
    }

    if (Array.isArray(payload?.curricula)) {
        return payload.curricula;
    }

    if (
        Array.isArray(
            payload?.data?.curricula
        )
    ) {
        return payload.data.curricula;
    }

    return [];
};


const getCount = (
    record,
    keys
) => {
    for (const key of keys) {
        const value = record?.[key];

        if (typeof value === "number") {
            return value;
        }

        if (
            typeof value === "string" &&
            value.trim() !== ""
        ) {
            const numeric =
                Number(value);

            if (
                !Number.isNaN(numeric)
            ) {
                return numeric;
            }
        }

        if (Array.isArray(value)) {
            return value.length;
        }

        if (
            value &&
            typeof value === "object"
        ) {
            if (
                typeof value.count ===
                "number"
            ) {
                return value.count;
            }

            if (
                Array.isArray(
                    value.results
                )
            ) {
                return value.results.length;
            }

            if (
                Array.isArray(
                    value.items
                )
            ) {
                return value.items.length;
            }
        }
    }

    return 0;
};


const formatDate = (value) => {
    if (!value) {
        return "—";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "—";
    }

    return date.toLocaleDateString();
};


const getSourceId = (record) => {
    return (
        record?.source_id ||
        record?.source?.id ||
        record?.source?.pk ||
        record?.id
    );
};


// ============================================================
// SUBJECT HELPERS
// ============================================================

const getSubjectName = (
    period
) => {
    if (!period) {
        return null;
    }

    if (
        period?.subject &&
        typeof period.subject ===
            "object"
    ) {
        return (
            period.subject?.name ||
            period.subject?.title ||
            null
        );
    }

    if (
        period?.subject_name
    ) {
        return period.subject_name;
    }

    if (
        typeof period?.subject ===
        "string"
    ) {
        return period.subject;
    }

    return null;
};


// ============================================================
// PERIOD TOPICS
// ============================================================

const getPeriodTopics = (
    period
) => {
    const possibleTopics = [
        period?.topics,
        period?.curriculum_topics,
        period?.data?.topics,
        period?.data?.curriculum_topics,
    ];

    for (
        const value of possibleTopics
    ) {
        if (Array.isArray(value)) {
            return value;
        }

        if (
            value &&
            typeof value ===
                "object"
        ) {
            if (
                Array.isArray(
                    value.results
                )
            ) {
                return value.results;
            }

            if (
                Array.isArray(
                    value.items
                )
            ) {
                return value.items;
            }
        }
    }

    return [];
};


// ============================================================
// EXTRACT SUBJECTS FROM PREVIEW
// ============================================================

const extractSubjectsFromPreview = (
    sourcePreview
) => {
    const possibleValues = [
        sourcePreview?.subjects,
        sourcePreview?.data?.subjects,
        sourcePreview?.preview?.subjects,
        sourcePreview?.preview
            ?.data?.subjects,
    ];

    for (
        const value of possibleValues
    ) {
        if (!Array.isArray(value)) {
            continue;
        }

        const subjects =
            value
                .map((item) => {
                    if (
                        typeof item ===
                        "string"
                    ) {
                        return item.trim();
                    }

                    if (
                        item &&
                        typeof item ===
                            "object"
                    ) {
                        return (
                            item.name ||
                            item.subject_name ||
                            item.title ||
                            item.subject
                                ?.name ||
                            item.subject
                                ?.title ||
                            null
                        );
                    }

                    return null;
                })
                .filter(Boolean)
                .map((item) =>
                    String(item).trim()
                )
                .filter(Boolean);

        if (subjects.length) {
            return [
                ...new Set(subjects),
            ];
        }
    }

    return [];
};


// ============================================================
// EXTRACT SOURCE SUBJECT
// ============================================================

const extractSourceSubject = (
    sourcePreview,
    source
) => {
    const value =
        sourcePreview?.source_subject ||
        sourcePreview?.data
            ?.source_subject ||
        sourcePreview?.preview
            ?.source_subject ||
        sourcePreview?.preview
            ?.data?.source_subject ||
        source?.source_subject ||
        source?.subject_name;

    if (!value) {
        return [];
    }

    if (Array.isArray(value)) {
        return value
            .map((item) => {
                if (
                    typeof item ===
                    "string"
                ) {
                    return item.trim();
                }

                if (
                    item &&
                    typeof item ===
                        "object"
                ) {
                    return (
                        item.name ||
                        item.subject_name ||
                        item.title ||
                        item.subject
                            ?.name ||
                        item.subject
                            ?.title ||
                        null
                    );
                }

                return null;
            })
            .filter(Boolean)
            .map((item) =>
                String(item).trim()
            )
            .filter(Boolean);
    }

    return String(value)
        .split(",")
        .map((item) =>
            item.trim()
        )
        .filter(Boolean);
};


// ============================================================
// EXTRACT PERIODS
// ============================================================

const extractSourcePeriods = (
    sourcePreview
) => {
    const possibleValues = [
        sourcePreview?.periods,
        sourcePreview?.data?.periods,
        sourcePreview?.preview?.periods,
        sourcePreview?.preview
            ?.data?.periods,
    ];

    for (
        const value of possibleValues
    ) {
        if (Array.isArray(value)) {
            return value;
        }

        if (
            value &&
            typeof value ===
                "object"
        ) {
            if (
                Array.isArray(
                    value.results
                )
            ) {
                return value.results;
            }

            if (
                Array.isArray(
                    value.items
                )
            ) {
                return value.items;
            }
        }
    }

    return [];
};


// ============================================================
// EXTRACT TOPICS
// ============================================================

const extractSourceTopics = (
    sourcePreview
) => {
    const possibleValues = [
        sourcePreview?.topics,
        sourcePreview?.data?.topics,
        sourcePreview?.preview?.topics,
        sourcePreview?.preview
            ?.data?.topics,
    ];

    for (
        const value of possibleValues
    ) {
        if (Array.isArray(value)) {
            return value;
        }

        if (
            value &&
            typeof value ===
                "object"
        ) {
            if (
                Array.isArray(
                    value.results
                )
            ) {
                return value.results;
            }

            if (
                Array.isArray(
                    value.items
                )
            ) {
                return value.items;
            }
        }
    }

    return [];
};


// ============================================================
// EXTRACT GRADES
// ============================================================

const extractSourceGrades = (
    sourcePreview
) => {
    const possibleValues = [
        sourcePreview?.grades,
        sourcePreview?.data?.grades,
        sourcePreview?.preview?.grades,
        sourcePreview?.preview
            ?.data?.grades,
    ];

    for (
        const value of possibleValues
    ) {
        if (Array.isArray(value)) {
            return value;
        }

        if (
            value &&
            typeof value ===
                "object"
        ) {
            if (
                Array.isArray(
                    value.results
                )
            ) {
                return value.results;
            }

            if (
                Array.isArray(
                    value.items
                )
            ) {
                return value.items;
            }
        }
    }

    return [];
};


// ============================================================
// BUILD SOURCE ROW
// ============================================================

const buildSourceRow = ({
    curriculum,
    source,
    sourcePreview,
}) => {
    const curriculumId =
        curriculum?.id ??
        curriculum?.pk;

    const sourceId =
        source?.id ??
        source?.pk;



    const periods =
        extractSourcePeriods(
            sourcePreview
        );

    const topics =
        extractSourceTopics(
            sourcePreview
        );

    const grades =
        extractSourceGrades(
            sourcePreview
        );


    // ========================================================
    // SUBJECTS
    // ========================================================

    let subjects =
        extractSourceSubject(
            sourcePreview,
            source
        );

    if (!subjects.length) {
        subjects =
            extractSubjectsFromPreview(
                sourcePreview
            );
    }

    if (
        !subjects.length &&
        periods.length
    ) {
        periods.forEach(
            (period) => {
                const subjectName =
                    getSubjectName(
                        period
                    );

                if (
                    subjectName &&
                    !subjects.includes(
                        subjectName
                    )
                ) {
                    subjects.push(
                        subjectName
                    );
                }
            }
        );
    }

    subjects = [
        ...new Set(
            subjects
                .map((subject) =>
                    String(
                        subject
                    ).trim()
                )
                .filter(Boolean)
        ),
    ];

    const subjectText =
        subjects.length
            ? subjects.join(", ")
            : "—";


    // ========================================================
    // GRADES
    // ========================================================

    const totalGrades =
        Number(
            sourcePreview?.total_grades ??
                sourcePreview?.data
                    ?.total_grades ??
                grades.length
        ) || 0;


    // ========================================================
    // SEMESTERS
    // ========================================================

    let totalSemesters =
        Number(
            sourcePreview?.total_semesters ??
                sourcePreview?.data
                    ?.total_semesters
        ) || 0;

    if (
        totalSemesters === 0
    ) {
        const semesterValues =
            new Set();

        periods.forEach(
            (period) => {
                const semester =
                    period?.semester_display ||
                    period?.semester;

                if (semester) {
                    semesterValues.add(
                        String(
                            semester
                        )
                    );
                }
            }
        );

        totalSemesters =
            semesterValues.size;
    }


    // ========================================================
    // PERIODS
    // ========================================================

    let totalPeriods =
        Number(
            sourcePreview?.total_periods ??
                sourcePreview?.data
                    ?.total_periods
        ) || 0;

    if (
        totalPeriods === 0
    ) {
        totalPeriods =
            periods.length;
    }


    // ========================================================
    // TOPICS
    // ========================================================

    let totalTopics =
        Number(
            sourcePreview?.total_topics ??
                sourcePreview?.data
                    ?.total_topics
        ) || 0;

    if (
        totalTopics === 0 &&
        topics.length > 0
    ) {
        totalTopics =
            topics.length;
    }

    if (
        totalTopics === 0 &&
        periods.length > 0
    ) {
        totalTopics =
            periods.reduce(
                (
                    total,
                    period
                ) =>
                    total +
                    getPeriodTopics(
                        period
                    ).length,
                0
            );
    }


    // ========================================================
    // IMPORT STATUS
    // ========================================================

    const isImported =
        source?.is_imported ===
            true ||
        sourcePreview
            ?.is_imported === true ||
        sourcePreview?.imported ===
            true ||
        totalPeriods > 0 ||
        totalTopics > 0;


    // ========================================================
    // DOCUMENT TITLE
    // ========================================================

    const documentTitle =
        source?.title ||
        source?.name ||
        source?.file_name ||
        source?.uploaded_file
            ?.split("/")
            .pop() ||
        "—";


    // ========================================================
    // FINAL ROW
    // ========================================================

    return {
        id:
            `source-${sourceId}`,

        source_id:
            sourceId,

        curriculum_id:
            curriculumId,

        name:
            curriculum?.name ||
            "Liberia National Curriculum",

        country:
            curriculum?.country ||
            "Liberia",

        is_active:
            curriculum?.is_active,

        is_approved:
            curriculum?.is_approved,

        source,

        source_preview:
            sourcePreview,

        title:
            documentTitle,

        document_title:
            documentTitle,

        subject_name:
            subjectText,

        subjects,

        total_grades:
            totalGrades,

        total_semesters:
            totalSemesters,

        semesters:
            totalSemesters,

        total_periods:
            totalPeriods,

        total_topics:
            totalTopics,

        periods,

        topics,

        updated_at:
            source?.updated_at ||
            source?.created_at ||
            curriculum?.updated_at,

        source_imported:
            isImported,
    };
};


// ============================================================
// COMPONENT
// ============================================================

const CurriculumAdminUpload = () => {
    const [form] =
        Form.useForm();

    const {
        selectedYear,
    } = useAcademicYear();


    // ========================================================
    // COLORS
    // ========================================================

    const colors = {
        primary: "#f97316",
        text: "#111827",
        secondary: "#6b7280",
        border: "#e5e7eb",
        success: "#16a34a",
        warning: "#d97706",
        danger: "#dc2626",
        background: "#f9fafb",
    };


    // ========================================================
    // UPLOAD STATE
    // ========================================================

    const [source, setSource] =
        useState(null);

    const [preview, setPreview] =
        useState(null);

    const [importResult, setImportResult] =
        useState(null);

    const [uploaded, setUploaded] =
        useState(false);

    const [imported, setImported] =
        useState(false);

    const [uploading, setUploading] =
        useState(false);


    // ========================================================
    // PREVIEW STATE
    // ========================================================

    const [previewLoading, setPreviewLoading] =
        useState(false);

    /*
     * IMPORTANT:
     *
     * This stores the ID of ONLY the source whose
     * preview is currently loading.
     *
     * Example:
     *
     * Math source = 12
     * Science source = 13
     *
     * Clicking Math:
     *
     * previewLoadingId = 12
     *
     * Therefore only Math gets loading=true.
     */

    const [previewLoadingId, setPreviewLoadingId] =
        useState(null);


    const [importLoading, setImportLoading] =
        useState(false);

    const [previewVisible, setPreviewVisible] =
        useState(false);


    // ========================================================
    // MESSAGES
    // ========================================================

    const [errorMessage, setErrorMessage] =
        useState("");

    const [successMessage, setSuccessMessage] =
        useState("");


    // ========================================================
    // TABLE STATE
    // ========================================================

    const [curricula, setCurricula] =
        useState([]);

    const [curriculumLoading, setCurriculumLoading] =
        useState(false);

    const [deleteLoadingId, setDeleteLoadingId] =
        useState(null);


    // ========================================================
    // STEPS
    // ========================================================

    const [currentStep, setCurrentStep] =
        useState(0);


    // ========================================================
    // LOAD CURRICULA
    // ========================================================

    const loadCurricula =
        useCallback(
            async () => {
                try {
                    setCurriculumLoading(
                        true
                    );

                    const yearId =
                        selectedYear?.id;

                    if (!yearId) {
                        setCurricula([]);
                        return;
                    }

                    const curriculumResponse =
                        await api.get(
                            `/api/curricula/?academic_year=${yearId}`
                        );

                    const curriculumList =
                        extractList(
                            curriculumResponse?.data
                        );

                    const allRows = [];

                    for (
                        const curriculum
                        of curriculumList
                    ) {
                        const curriculumId =
                            curriculum?.id ??
                            curriculum?.pk;

                        if (!curriculumId) {
                            continue;
                        }

                        try {
                            const sourcesResponse =
                                await api.get(
                                    `/api/sources/?curriculum=${curriculumId}`
                                );

                            const sources =
                                extractList(
                                    sourcesResponse?.data
                                );

                            /*
                             * ONE PDF = ONE ROW
                             */

                            for (
                                const currentSource
                                of sources
                            ) {
                                const sourceId =
                                    currentSource?.id ??
                                    currentSource?.pk;

                                if (!sourceId) {
                                    continue;
                                }

                                let sourcePreview =
                                    null;

                                try {
                                    const previewResponse =
                                        await api.get(
                                            `/api/sources/${sourceId}/preview/`
                                        );

                                    sourcePreview =
                                        previewResponse?.data ||
                                        null;
                                } catch (
                                    previewError
                                ) {
                                    console.error(
                                        `Failed to preview source ${sourceId}:`,
                                        previewError
                                    );
                                }

                                const row =
                                    buildSourceRow({
                                        curriculum,
                                        source:
                                            currentSource,
                                        sourcePreview,
                                    });

                                allRows.push(
                                    row
                                );
                            }
                        } catch (
                            sourceError
                        ) {
                            console.error(
                                `Failed to load sources for curriculum ${curriculumId}:`,
                                sourceError
                            );
                        }
                    }

                    allRows.sort(
                        (a, b) =>
                            String(
                                a.source_id
                            ).localeCompare(
                                String(
                                    b.source_id
                                ),
                                undefined,
                                {
                                    numeric: true,
                                }
                            )
                    );

                    setCurricula(
                        allRows
                    );
                } catch (error) {
                    console.error(
                        "Failed to load curricula:",
                        error
                    );

                    setCurricula([]);

                    await Swal.fire({
                        icon: "error",
                        title:
                            "Failed to Load Curricula",
                        text:
                            error?.response
                                ?.data
                                ?.detail ||
                            error?.response
                                ?.data
                                ?.message ||
                            "Unable to load curriculum records.",
                    });
                } finally {
                    setCurriculumLoading(
                        false
                    );
                }
            },
            [
                selectedYear?.id,
            ]
        );


    // ========================================================
    // LOAD WHEN YEAR CHANGES
    // ========================================================

    useEffect(() => {
        if (!selectedYear?.id) {
            setCurricula([]);
            return;
        }

        loadCurricula();
    }, [
        selectedYear?.id,
        loadCurricula,
    ]);


    // ========================================================
    // SET ACADEMIC YEAR
    // ========================================================

    useEffect(() => {
        if (!selectedYear?.id) {
            return;
        }

        form.setFieldsValue({
            academic_year:
                selectedYear.name,
        });
    }, [
        selectedYear?.id,
        selectedYear?.name,
        form,
    ]);


    // ========================================================
    // RESET
    // ========================================================

    const resetUpload =
        useCallback(
            () => {
                setSource(null);

                setPreview(null);

                setImportResult(null);

                setUploaded(false);

                setImported(false);

                setPreviewVisible(false);

                setPreviewLoading(false);

                setPreviewLoadingId(null);

                setErrorMessage("");

                setSuccessMessage("");

                setCurrentStep(0);

                form.setFieldsValue({
                    academic_year:
                        selectedYear?.name ||
                        "",

                    title: "",

                    document_type:
                        "Curriculum",
                });
            },
            [
                form,
                selectedYear?.name,
            ]
        );


    // ========================================================
    // PREVIEW
    // ========================================================

    const handlePreview =
        useCallback(
            async (
                sourceId,
                openPopup = true
            ) => {
                if (!sourceId) {
                    return;
                }

                try {
                    /*
                     * IMPORTANT:
                     * Set the exact source ID BEFORE
                     * starting the request.
                     */

                    setPreviewLoading(
                        true
                    );

                    setPreviewLoadingId(
                        sourceId
                    );

                    setErrorMessage("");

                    /*
                     * Find the exact table row.
                     */

                    const matchingRow =
                        curricula.find(
                            (item) =>
                                String(
                                    getSourceId(
                                        item
                                    )
                                ) ===
                                String(
                                    sourceId
                                )
                        );

                    /*
                     * Make sure popup operates on
                     * the exact source being previewed.
                     */

                    if (
                        matchingRow?.source
                    ) {
                        setSource(
                            matchingRow.source
                        );

                        setUploaded(
                            true
                        );

                        setImported(
                            matchingRow.source_imported ===
                                true ||
                            matchingRow.source
                                ?.is_imported ===
                                true
                        );

                        setImportResult(
                            null
                        );
                    }

                    /*
                     * Request preview for THIS source.
                     */

                    const response =
                        await api.get(
                            `/api/sources/${sourceId}/preview/`
                        );

                    const data =
                        response?.data ||
                        {};

                    /*
                     * Store only this source's
                     * preview.
                     */

                    setPreview(data);

                    if (openPopup) {
                        setPreviewVisible(
                            true
                        );
                    }

                    setCurrentStep(1);
                } catch (error) {
                    const text =
                        getErrorMessage(
                            error,
                            "Unable to generate curriculum preview."
                        );

                    setErrorMessage(
                        text
                    );

                    await Swal.fire({
                        icon: "error",
                        title:
                            "Preview failed",
                        text,
                    });
                } finally {
                    /*
                     * Clear ONLY after the request
                     * for this source finishes.
                     */

                    setPreviewLoading(
                        false
                    );

                    setPreviewLoadingId(
                        null
                    );
                }
            },
            [
                curricula,
            ]
        );


    // ========================================================
    // UPLOAD
    // ========================================================

    const handleUpload =
        async ({
            file,
            onSuccess,
            onError,
        }) => {
            try {
                if (!selectedYear?.id) {
                    throw new Error(
                        "Please select an academic year first."
                    );
                }

                const actualFile =
                    file?.originFileObj ||
                    file;

                if (!actualFile) {
                    throw new Error(
                        "No curriculum PDF was selected."
                    );
                }

                const isPdf =
                    actualFile.type ===
                        "application/pdf" ||
                    actualFile.name
                        ?.toLowerCase()
                        .endsWith(
                            ".pdf"
                        );

                if (!isPdf) {
                    throw new Error(
                        "Only PDF curriculum documents are allowed."
                    );
                }

                setUploading(true);

                setErrorMessage("");

                setSuccessMessage("");

                const curriculumResponse =
                    await api.get(
                        "/api/curricula/",
                        {
                            params: {
                                academic_year:
                                    selectedYear.id,
                            },
                        }
                    );

                const curriculumList =
                    extractList(
                        curriculumResponse?.data
                    );

                let curriculum =
                    curriculumList.find(
                        (item) =>
                            item?.name
                                ?.trim()
                                .toLowerCase() ===
                            "liberia national curriculum"
                    );

                if (!curriculum?.id) {
                    const createResponse =
                        await api.post(
                            "/api/curricula/",
                            {
                                academic_year:
                                    selectedYear.id,

                                name:
                                    "Liberia National Curriculum",

                                country:
                                    "Liberia",

                                version:
                                    "",

                                description:
                                    "",

                                is_active:
                                    true,

                                is_approved:
                                    false,
                            }
                        );

                    curriculum =
                        createResponse?.data;

                    if (
                        curriculum?.data &&
                        !curriculum?.id
                    ) {
                        curriculum =
                            curriculum.data;
                    }

                    if (
                        curriculum?.curriculum &&
                        !curriculum?.id
                    ) {
                        curriculum =
                            curriculum.curriculum;
                    }
                }

                if (!curriculum?.id) {
                    throw new Error(
                        "Unable to find or create the Liberia National Curriculum."
                    );
                }

                const formData =
                    new FormData();

                formData.append(
                    "curriculum",
                    String(
                        curriculum.id
                    )
                );

                formData.append(
                    "title",
                    actualFile.name
                );

                formData.append(
                    "document_type",
                    "Curriculum"
                );

                formData.append(
                    "uploaded_file",
                    actualFile
                );

                const response =
                    await api.post(
                        "/api/sources/",
                        formData
                    );

                const responseData =
                    response?.data ||
                    {};

                const normalizedSource =
                    responseData.source ||
                    responseData.data ||
                    responseData;

                if (
                    !normalizedSource?.id
                ) {
                    throw new Error(
                        "Upload succeeded, but no curriculum source ID was returned."
                    );
                }

                setSource(
                    normalizedSource
                );

                setUploaded(true);

                setImported(false);

                setImportResult(
                    null
                );

                setCurrentStep(1);

                onSuccess?.(
                    normalizedSource,
                    actualFile.name
                );

                await loadCurricula();

                await Swal.fire({
                    toast: true,
                    position:
                        "top-end",
                    icon: "success",
                    title:
                        "Curriculum PDF uploaded",
                    showConfirmButton:
                        false,
                    timer: 1800,
                    timerProgressBar:
                        true,
                });

                await handlePreview(
                    normalizedSource.id,
                    true
                );
            } catch (error) {
                console.error(
                    "CURRICULUM SOURCE UPLOAD ERROR:",
                    error
                );

                const text =
                    getErrorMessage(
                        error,
                        "Unable to upload curriculum source."
                    );

                setErrorMessage(
                    text
                );

                onError?.(
                    error
                );

                await Swal.fire({
                    icon: "error",
                    title:
                        "Curriculum upload failed",
                    text,
                });
            } finally {
                setUploading(false);
            }
        };


    // ========================================================
    // IMPORT
    // ========================================================

    const executeImport =
        async () => {
            if (!source?.id) {
                return;
            }

            try {
                setImportLoading(
                    true
                );

                setErrorMessage("");

                setSuccessMessage("");

                const response =
                    await api.post(
                        `/api/sources/${source.id}/import/`
                    );

                const data =
                    response?.data ||
                    {};

                setImportResult(
                    data
                );

                setImported(true);

                setCurrentStep(2);

                setPreviewVisible(
                    false
                );

                setSuccessMessage(
                    "Curriculum imported successfully."
                );

                await loadCurricula();

                await Swal.fire({
                    toast: true,
                    position:
                        "top-end",
                    icon: "success",
                    title:
                        "Curriculum imported successfully",
                    showConfirmButton:
                        false,
                    timer: 2200,
                    timerProgressBar:
                        true,
                });
            } catch (error) {
                const text =
                    getErrorMessage(
                        error,
                        "Unable to import curriculum."
                    );

                setErrorMessage(
                    text
                );

                await Swal.fire({
                    icon: "error",
                    title:
                        "Import failed",
                    text,
                });
            } finally {
                setImportLoading(
                    false
                );
            }
        };


    // ========================================================
    // IMPORT CONFIRMATION
    // ========================================================

    const handleImport =
        async () => {
            if (!source?.id) {
                await Swal.fire({
                    icon: "warning",
                    title:
                        "No curriculum source",
                    text:
                        "Please upload a curriculum PDF first.",
                });

                return;
            }

            if (imported) {
                await Swal.fire({
                    icon: "info",
                    title:
                        "Already imported",
                    text:
                        "This curriculum has already been imported.",
                });

                return;
            }

            const result =
                await Swal.fire({
                    title:
                        "Confirm Curriculum Import",

                    text:
                        "Please review the curriculum preview. Importing will save the extracted subjects, periods and topics into the database.",

                    icon: "warning",

                    showCancelButton:
                        true,

                    confirmButtonText:
                        "Confirm & Import",

                    cancelButtonText:
                        "Cancel",

                    reverseButtons:
                        true,

                    focusCancel:
                        true,
                });

            if (
                result.isConfirmed
            ) {
                await executeImport();
            }
        };


    // ========================================================
    // DELETE
    // ========================================================

    const handleDeleteCurriculum =
        useCallback(
            async (record) => {
                const sourceId =
                    getSourceId(
                        record
                    );

                if (!sourceId) {
                    return;
                }

                const sourceTitle =
                    record?.source
                        ?.title ||
                    record?.source
                        ?.name ||
                    record?.title ||
                    "Curriculum";

                const result =
                    await Swal.fire({
                        title:
                            "Delete curriculum?",

                        text:
                            `This will permanently delete "${sourceTitle}" and this uploaded curriculum source only.`,

                        icon: "warning",

                        showCancelButton:
                            true,

                        confirmButtonText:
                            "Yes, delete",

                        cancelButtonText:
                            "Cancel",

                        reverseButtons:
                            true,

                        focusCancel:
                            true,
                    });

                if (
                    !result.isConfirmed
                ) {
                    return;
                }

                try {
                    setDeleteLoadingId(
                        sourceId
                    );

                    await api.delete(
                        `/api/sources/${sourceId}/`
                    );

                    setCurricula(
                        (previous) =>
                            previous.filter(
                                (item) =>
                                    String(
                                        getSourceId(
                                            item
                                        )
                                    ) !==
                                    String(
                                        sourceId
                                    )
                            )
                    );

                    if (
                        String(
                            source?.id
                        ) ===
                        String(
                            sourceId
                        )
                    ) {
                        resetUpload();
                    }

                    await Swal.fire({
                        icon: "success",

                        title:
                            "Deleted",

                        text:
                            "Uploaded curriculum PDF deleted successfully.",

                        timer: 1600,

                        showConfirmButton:
                            false,
                    });
                } catch (error) {
                    const text =
                        getErrorMessage(
                            error,
                            "Unable to delete curriculum."
                        );

                    setErrorMessage(
                        text
                    );

                    await Swal.fire({
                        icon: "error",

                        title:
                            "Delete failed",

                        text,
                    });
                } finally {
                    setDeleteLoadingId(
                        null
                    );
                }
            },
            [
                resetUpload,
                source,
            ]
        );


    // ========================================================
    // TABLE COLUMNS
    // ========================================================

    const curriculumColumns =
        useMemo(
            () => [
                // ====================================================
                // CURRICULUM
                // ====================================================

                {
                    title:
                        "Curriculum",

                    key:
                        "name",

                    fixed:
                        "left",

                    width:
                        240,

                    render: (
                        _,
                        record
                    ) => (
                        <Space
                            direction="vertical"
                            size={0}
                        >
                            <Text strong>
                                {record?.name ||
                                    "—"}
                            </Text>

                            {record?.country && (
                                <Text
                                    type="secondary"
                                    style={{
                                        fontSize:
                                            12,
                                    }}
                                >
                                    {
                                        record.country
                                    }
                                </Text>
                            )}
                        </Space>
                    ),
                },


                // ====================================================
                // SUBJECT
                // ====================================================

                {
                    title:
                        "Subject",

                    key:
                        "subject",

                    width:
                        220,

                    align:
                        "center",

                    render: (
                        _,
                        record
                    ) => {
                        const subjects =
                            Array.isArray(
                                record?.subjects
                            )
                                ? record.subjects
                                : String(
                                      record?.subject_name ||
                                          ""
                                  )
                                      .split(",")
                                      .map(
                                          (
                                              item
                                          ) =>
                                              item.trim()
                                      )
                                      .filter(
                                          Boolean
                                      );

                        if (
                            !subjects.length
                        ) {
                            return "—";
                        }

                        return (
                            <Space
                                wrap
                                size={[
                                    4,
                                    4,
                                ]}
                                style={{
                                    justifyContent:
                                        "center",
                                }}
                            >
                                {subjects.map(
                                    (
                                        subject
                                    ) => (
                                        <Tag
                                            key={
                                                subject
                                            }
                                            color="blue"
                                        >
                                            {
                                                subject
                                            }
                                        </Tag>
                                    )
                                )}
                            </Space>
                        );
                    },
                },


                // ====================================================
                // GRADES
                // ====================================================

                {
                    title:
                        "Grades",

                    key:
                        "total_grades",

                    width:
                        85,

                    align:
                        "center",

                    render: (
                        _,
                        record
                    ) =>
                        getCount(
                            record,
                            [
                                "total_grades",
                                "grade_count",
                                "grades_count",
                                "grades",
                            ]
                        ),
                },


                // ====================================================
                // SEMESTERS
                // ====================================================

                {
                    title:
                        "Semesters",

                    key:
                        "semesters",

                    width:
                        110,

                    align:
                        "center",

                    render: (
                        _,
                        record
                    ) => {
                        const semesters =
                            Number(
                                record?.total_semesters
                            ) ||
                            Number(
                                record?.semesters
                            ) ||
                            0;

                        return (
                            <Tag
                                color="purple"
                            >
                                {
                                    semesters
                                }
                            </Tag>
                        );
                    },
                },


                // ====================================================
                // PERIODS
                // ====================================================

                {
                    title:
                        "Periods",

                    key:
                        "total_periods",

                    width:
                        90,

                    align:
                        "center",

                    render: (
                        _,
                        record
                    ) =>
                        getCount(
                            record,
                            [
                                "total_periods",
                                "period_count",
                                "periods_count",
                                "periods",
                            ]
                        ),
                },


                // ====================================================
                // TOPICS
                // ====================================================

                {
                    title:
                        "Topics",

                    key:
                        "total_topics",

                    width:
                        90,

                    align:
                        "center",

                    render: (
                        _,
                        record
                    ) =>
                        getCount(
                            record,
                            [
                                "total_topics",
                                "topic_count",
                                "topics_count",
                                "topics",
                            ]
                        ),
                },


                // ====================================================
                // STATUS
                // ====================================================

                {
                    title:
                        "Status",

                    key:
                        "status",

                    width:
                        110,

                    align:
                        "center",

                    render: (
                        _,
                        record
                    ) => {
                        const periods =
                            getCount(
                                record,
                                [
                                    "total_periods",
                                    "period_count",
                                    "periods_count",
                                    "periods",
                                ]
                            );

                        const topics =
                            getCount(
                                record,
                                [
                                    "total_topics",
                                    "topic_count",
                                    "topics_count",
                                    "topics",
                                ]
                            );

                        if (
                            record?.is_approved
                        ) {
                            return (
                                <Tag
                                    color="green"
                                >
                                    Approved
                                </Tag>
                            );
                        }

                        if (
                            record?.is_active ===
                            false
                        ) {
                            return (
                                <Tag
                                    color="red"
                                >
                                    Inactive
                                </Tag>
                            );
                        }

                        if (
                            periods > 0 ||
                            topics > 0
                        ) {
                            return (
                                <Tag
                                    color="blue"
                                >
                                    Imported
                                </Tag>
                            );
                        }

                        return (
                            <Tag
                                color="gold"
                            >
                                Uploaded
                            </Tag>
                        );
                    },
                },


                // ====================================================
                // UPDATED
                // ====================================================

                {
                    title:
                        "Updated",

                    key:
                        "updated_at",

                    width:
                        120,

                    render: (
                        _,
                        record
                    ) =>
                        formatDate(
                            record?.updated_at
                        ),
                },


                // ====================================================
                // ACTION
                // ====================================================

                {
                    title:
                        "Action",

                    key:
                        "action",

                    fixed:
                        "right",

                    width:
                        110,

                    align:
                        "center",

                    render: (
                        _,
                        record
                    ) => {
                        const sourceId =
                            getSourceId(
                                record
                            );

                        /*
                         * EXACT ROW LOADING CHECK
                         *
                         * Math sourceId  = 12
                         * Science sourceId = 13
                         *
                         * If Math is loading:
                         *
                         * previewLoadingId = 12
                         *
                         * Math:
                         * 12 === 12 => true
                         *
                         * Science:
                         * 12 === 13 => false
                         */

                        const isThisRowPreviewing =
                            String(
                                previewLoadingId
                            ) ===
                            String(
                                sourceId
                            );

                        const isThisRowDeleting =
                            String(
                                deleteLoadingId
                            ) ===
                            String(
                                sourceId
                            );

                        return (
                            <Space
                                size={2}
                            >
                                {/* ======================================
                                    EYE / PREVIEW
                                ====================================== */}

                                <Tooltip
                                    title="View curriculum preview"
                                >
                                    <Button
                                        type="text"

                                        icon={
                                            <EyeOutlined />
                                        }

                                        loading={
                                            isThisRowPreviewing
                                        }

                                        disabled={
                                            previewLoading &&
                                            !isThisRowPreviewing
                                        }

                                        onClick={() =>
                                            handlePreview(
                                                sourceId,
                                                true
                                            )
                                        }
                                    />
                                </Tooltip>


                                {/* ======================================
                                    DELETE
                                ====================================== */}

                                <Tooltip
                                    title="Delete curriculum PDF"
                                >
                                    <Button
                                        danger
                                        type="text"

                                        icon={
                                            <DeleteOutlined />
                                        }

                                        loading={
                                            isThisRowDeleting
                                        }

                                        onClick={() =>
                                            handleDeleteCurriculum(
                                                record
                                            )
                                        }
                                    />
                                </Tooltip>
                            </Space>
                        );
                    },
                },
            ],
            [
                deleteLoadingId,
                handleDeleteCurriculum,
                handlePreview,
                previewLoading,
                previewLoadingId,
            ]
        );


    // ========================================================
    // STEPS
    // ========================================================

    const steps = [
        {
            title:
                "Upload",

            description:
                "Upload curriculum PDF",
        },

        {
            title:
                "Preview",

            description:
                "Review extracted content",
        },

        {
            title:
                "Complete",

            description:
                "Curriculum imported",
        },
    ];


    // ========================================================
    // RENDER
    // ========================================================

    return (
        <div
            style={{
                padding: 0,
            }}
        >
            {/* ==================================================
                HEADER
            ================================================== */}

            <Card
                bordered={false}

                style={{
                    borderRadius: 12,

                    marginBottom:
                        24,

                    border:
                        `1px solid ${colors.border}`,
                }}
            >
                <Space
                    direction="vertical"
                    size={4}
                    style={{
                        width:
                            "100%",
                    }}
                >
                    <Title
                        level={3}
                        style={{
                            margin: 0,
                            color:
                                colors.text,
                        }}
                    >
                        Curriculum Administration
                    </Title>

                    <Text
                        type="secondary"
                    >
                        Upload, preview and
                        import curriculum
                        documents.
                    </Text>
                </Space>

                <Divider />

                <Steps
                    current={
                        currentStep
                    }

                    items={
                        steps
                    }

                    responsive
                />
            </Card>


            {/* ==================================================
                ERROR
            ================================================== */}

            {errorMessage && (
                <Alert
                    type="error"
                    showIcon
                    closable

                    message={
                        errorMessage
                    }

                    onClose={() =>
                        setErrorMessage(
                            ""
                        )
                    }

                    style={{
                        marginBottom:
                            24,

                        whiteSpace:
                            "pre-line",
                    }}
                />
            )}


            {/* ==================================================
                SUCCESS
            ================================================== */}

            {successMessage && (
                <Alert
                    type="success"
                    showIcon
                    closable

                    message={
                        successMessage
                    }

                    onClose={() =>
                        setSuccessMessage(
                            ""
                        )
                    }

                    style={{
                        marginBottom:
                            24,
                    }}
                />
            )}


            {/* ==================================================
                UPLOAD CARD
            ================================================== */}

            <Card
                size="small"

                title={
                    <Space>
                        <UploadOutlined
                            style={{
                                color:
                                    colors.primary,
                            }}
                        />

                        <span>
                            Upload Curriculum
                        </span>
                    </Space>
                }

                style={{
                    borderColor:
                        colors.border,

                    borderRadius:
                        12,

                    marginBottom:
                        24,
                }}
            >
                <Form
                    form={
                        form
                    }

                    layout="vertical"

                    initialValues={{
                        document_type:
                            "Curriculum",
                    }}
                >
                    <Row
                        gutter={[
                            16,
                            16,
                        ]}
                    >
                        <Col
                            xs={24}
                            md={8}
                        >
                            <Form.Item
                                label="Academic Year"
                                name="academic_year"
                            >
                                <Input
                                    disabled

                                    value={
                                        selectedYear?.name
                                    }
                                />
                            </Form.Item>
                        </Col>


                        <Col
                            xs={24}
                            md={8}
                        >
                            <Form.Item
                                label="Document Title"
                                name="title"
                            >
                                <Input
                                    placeholder="Curriculum document title"
                                />
                            </Form.Item>
                        </Col>


                        <Col
                            xs={24}
                            md={8}
                        >
                            <Form.Item
                                label="Document Type"
                                name="document_type"
                            >
                                <Input
                                    disabled
                                />
                            </Form.Item>
                        </Col>
                    </Row>


                    <Form.Item
                        style={{
                            marginBottom:
                                0,
                        }}
                    >
                        <Dragger
                            name="uploaded_file"

                            multiple={
                                false
                            }

                            maxCount={
                                1
                            }

                            accept=".pdf,application/pdf"

                            customRequest={
                                handleUpload
                            }

                            showUploadList={
                                true
                            }

                            disabled={
                                uploading ||
                                !selectedYear?.id
                            }

                            beforeUpload={(
                                file
                            ) => {
                                const isPdf =
                                    file.type ===
                                        "application/pdf" ||
                                    file.name
                                        ?.toLowerCase()
                                        .endsWith(
                                            ".pdf"
                                        );

                                if (
                                    !isPdf
                                ) {
                                    Swal.fire({
                                        icon:
                                            "warning",

                                        title:
                                            "Invalid file",

                                        text:
                                            "Please upload a PDF curriculum document.",
                                    });

                                    return Upload.LIST_IGNORE;
                                }

                                return true;
                            }}
                        >
                            {uploading ? (
                                <Space
                                    direction="vertical"
                                >
                                    <Spin />

                                    <Text>
                                        Uploading curriculum PDF...
                                    </Text>
                                </Space>
                            ) : (
                                <>
                                    <p className="ant-upload-drag-icon">
                                        <InboxOutlined
                                            style={{
                                                color:
                                                    colors.primary,
                                            }}
                                        />
                                    </p>

                                    <p className="ant-upload-text">
                                        Click or drag a curriculum PDF here
                                    </p>

                                    <p className="ant-upload-hint">
                                        Upload the curriculum PDF for the selected academic year.
                                    </p>
                                </>
                            )}
                        </Dragger>
                    </Form.Item>
                </Form>
            </Card>


            {/* ==================================================
                UPLOADED SOURCE
            ================================================== */}

            {uploaded &&
                source && (
                    <Card
                        size="small"

                        title={
                            <Space>
                                <FilePdfOutlined
                                    style={{
                                        color:
                                            colors.primary,
                                    }}
                                />

                                <span>
                                    Uploaded Curriculum
                                </span>

                                <Badge
                                    status={
                                        imported
                                            ? "success"
                                            : "processing"
                                    }

                                    text={
                                        imported
                                            ? "Imported"
                                            : "Ready for preview"
                                    }
                                />
                            </Space>
                        }

                        style={{
                            borderColor:
                                colors.border,

                            borderRadius:
                                12,

                            marginBottom:
                                24,
                        }}
                    >
                        <Descriptions
                            bordered
                            size="small"

                            column={{
                                xs: 1,
                                sm: 2,
                                md: 3,
                            }}
                        >
                            <Descriptions.Item label="Title">
                                {source.title ||
                                    source.name ||
                                    "—"}
                            </Descriptions.Item>


                            <Descriptions.Item label="File">
                                {source.file_name ||
                                    source.uploaded_file
                                        ?.split(
                                            "/"
                                        )
                                        .pop() ||
                                    "—"}
                            </Descriptions.Item>


                            <Descriptions.Item label="Academic Year">
                                {source.academic_year_name ||
                                    selectedYear?.name ||
                                    "—"}
                            </Descriptions.Item>


                            <Descriptions.Item label="Document Type">
                                {source.document_type ||
                                    "Curriculum"}
                            </Descriptions.Item>


                            <Descriptions.Item label="Source ID">
                                {source.id ||
                                    "—"}
                            </Descriptions.Item>


                            <Descriptions.Item label="Status">
                                {imported ? (
                                    <Tag color="green">
                                        <CheckCircleFilled />
                                        {" "}
                                        Imported
                                    </Tag>
                                ) : (
                                    <Tag color="blue">
                                        Uploaded
                                    </Tag>
                                )}
                            </Descriptions.Item>
                        </Descriptions>


                        <Divider />


                        <Space wrap>
                            <Button
                                type="primary"

                                icon={
                                    <FilePdfOutlined />
                                }

                                loading={
                                    previewLoading &&
                                    String(
                                        previewLoadingId
                                    ) ===
                                        String(
                                            source?.id
                                        )
                                }

                                onClick={() =>
                                    handlePreview(
                                        source.id,
                                        true
                                    )
                                }
                            >
                                Review Preview
                            </Button>


                            {!imported && (
                                <Button
                                    onClick={
                                        resetUpload
                                    }
                                >
                                    Upload Another
                                </Button>
                            )}
                        </Space>
                    </Card>
                )}


            {/* ==================================================
                IMPORT RESULT
            ================================================== */}

            {imported &&
                importResult && (
                    <Card
                        size="small"

                        style={{
                            borderColor:
                                colors.border,

                            borderRadius:
                                12,

                            marginBottom:
                                24,
                        }}
                    >
                        <Result
                            status="success"

                            title="Curriculum Imported Successfully"

                            subTitle={
                                importResult?.curriculum_name ||
                                source?.curriculum_name ||
                                "Curriculum"
                            }

                            extra={
                                <Space
                                    wrap
                                >
                                    <Tag color="blue">
                                        Subjects:{" "}
                                        {importResult
                                            ?.database
                                            ?.subjects ??
                                            importResult
                                                ?.subjects ??
                                            0}
                                    </Tag>

                                    <Tag color="blue">
                                        Grades:{" "}
                                        {importResult
                                            ?.database
                                            ?.grades ??
                                            importResult
                                                ?.grades ??
                                            0}
                                    </Tag>

                                    <Tag color="blue">
                                        Periods:{" "}
                                        {importResult
                                            ?.database
                                            ?.periods ??
                                            importResult
                                                ?.periods ??
                                            0}
                                    </Tag>

                                    <Tag color="blue">
                                        Topics:{" "}
                                        {importResult
                                            ?.database
                                            ?.topics ??
                                            importResult
                                                ?.topics ??
                                            0}
                                    </Tag>
                                </Space>
                            }
                        />
                    </Card>
                )}


            {/* ==================================================
                CURRICULUM RECORDS
            ================================================== */}

            <Card
                size="small"

                title={
                    <Space>
                        <FilePdfOutlined
                            style={{
                                color:
                                    colors.primary,
                            }}
                        />

                        <span>
                            Curriculum Records
                        </span>

                        <Badge
                            count={
                                curricula.length
                            }

                            showZero

                            style={{
                                backgroundColor:
                                    colors.primary,
                            }}
                        />
                    </Space>
                }

                extra={
                    <Button
                        icon={
                            <ReloadOutlined />
                        }

                        onClick={() =>
                            loadCurricula()
                        }

                        loading={
                            curriculumLoading
                        }
                    >
                        Refresh
                    </Button>
                }

                style={{
                    borderColor:
                        colors.border,

                    borderRadius:
                        12,
                }}
            >
                {curricula.length ===
                    0 &&
                !curriculumLoading ? (
                    <Empty
                        description={
                            selectedYear?.id
                                ? "No curriculum has been uploaded for this academic year."
                                : "Select an academic year to view curriculum records."
                        }
                    />
                ) : (
                    <Table
                        rowKey={(
                            record
                        ) =>
                            record.id
                        }

                        loading={
                            curriculumLoading
                        }

                        dataSource={
                            curricula
                        }

                        columns={
                            curriculumColumns
                        }

                        size="middle"

                        scroll={{
                            x: 1450,
                        }}

                        pagination={{
                            pageSize:
                                10,

                            showSizeChanger:
                                true,

                            showTotal:
                                (
                                    total
                                ) =>
                                    `${total} curriculum${
                                        total ===
                                        1
                                            ? ""
                                            : "s"
                                    }`,
                        }}
                    />
                )}
            </Card>


            {/* ==================================================
                PREVIEW POPUP
            ================================================== */}

            <CurriculumPreviewPopup
                visible={
                    previewVisible
                }

                open={
                    previewVisible
                }

                loading={
                    previewLoading
                }

                preview={
                    preview
                }

                source={
                    source
                }

                importResult={
                    importResult
                }

                imported={
                    imported
                }

                importLoading={
                    importLoading
                }

                onClose={() =>
                    setPreviewVisible(
                        false
                    )
                }

                onCancel={() =>
                    setPreviewVisible(
                        false
                    )
                }

                onImport={
                    handleImport
                }

                onConfirmImport={
                    handleImport
                }
            />
        </div>
    );
};


export default CurriculumAdminUpload;